import React, { useState, useMemo } from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import RepeatIcon from "@mui/icons-material/Repeat";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider } from "@mui/material/styles";
import violetTheme from "../../../theme/rightworkTheme";

import {
  postRightworkAPI,
} from "../requests/utilities/requests";
import {
  API_post_recurring_maintenance_template,
  API_update_recurring_maintenance_template,
  API_delete_recurring_maintenance_template,
} from "../../../constants";
import {
  IssueTypeBadge,
  PriorityBadge,
  ISSUE_TYPE_OPTIONS,
  CATEGORY_ISSUE_TYPE_MAP,
  CATEGORY_CONFIG,
  PRIORITY_OPTIONS,
  RECURRENCE_INTERVAL_OPTIONS,
} from "./MaintenanceBadges";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonFilled from "../requests/shared/helpers/ButtonFilled";
import ButtonFilledCancel from "../requests/shared/helpers/ButtonFilledCancel";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";


const selectSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "45px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    "&.Mui-focused fieldset": {
      borderColor: "#6d28d9",
    },
  },
};

function getTemplateGroupKey(t) {
  return [
    t.title,
    t.description,
    t.issue_type,
    t.priority,
    t.recurrence_interval,
    t.recurrence_count,
    t.recurrence_trigger,
    t.start_date,
  ].join("||");
}

function groupTemplatesBySharedFields(templates) {
  const groups = {};
  templates.forEach((t) => {
    const key = getTemplateGroupKey(t);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(t);
  });
  return Object.entries(groups).map(([key, grouped]) => {
    const first = grouped[0];
    return {
      groupKey: key,
      title: first.title,
      templates: grouped,
      sharedFields: {
        description: first.description,
        issue_type: first.issue_type,
        priority: first.priority,
        recurrence_interval: first.recurrence_interval,
        recurrence_count: first.recurrence_count,
        recurrence_trigger: first.recurrence_trigger,
        start_date: first.start_date,
        cadence_display: first.cadence_display,
      },
      storeCount: grouped.length,
      isMultiStore: grouped.length > 1,
      allActive: grouped.every((t) => t.is_active),
      anyActive: grouped.some((t) => t.is_active),
      totalTicketCount: grouped.reduce((sum, t) => sum + (t.generated_ticket_count || 0), 0),
    };
  });
}

function RecurringManagementDrawer({
  isOpen,
  onClose,
  templates,
  isLoading,
  onRefresh,
  category,
  homeStore,
  storeNames,
  employees,
  onStoreChange,
  permissionRole,
  permissionOverrides,
  issueTypeOptions: issueTypeOptionsProp,
  priorityOptions: priorityOptionsProp,
  optionsStatus = "ready", // loading | ready | error — from the parent's options fetch
  onRetryOptions,
}) {
  const issueTypeOptions = issueTypeOptionsProp?.length ? issueTypeOptionsProp : (CATEGORY_ISSUE_TYPE_MAP[category] || ISSUE_TYPE_OPTIONS);
  const priorityOptions = priorityOptionsProp?.length ? priorityOptionsProp : PRIORITY_OPTIONS;
  const optionsReady = optionsStatus === "ready";
  const priorityLabelFor = (value) => priorityOptions.find((o) => o.value === value)?.label;

  const [editingGroup, setEditingGroup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [storesToAdd, setStoresToAdd] = useState([]);
  const [storesToRemove, setStoresToRemove] = useState([]);
  const [activeToggled, setActiveToggled] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({});

  const isManager = ["company_manager", "company_admin", "rightwork_internal"].includes(permissionRole);

  const groupedTemplates = useMemo(() => groupTemplatesBySharedFields(templates), [templates]);

  const effectiveStoreCount = editingGroup
    ? editingGroup.storeCount - storesToRemove.length + storesToAdd.length
    : 0;
  const effectiveIsMultiStore = effectiveStoreCount > 1;

  // Stores already in the group (not pending removal)
  const existingStoreIds = editingGroup
    ? editingGroup.templates
        .filter((t) => !storesToRemove.includes(t.uuid))
        .map((t) => t.store?.id)
    : [];

  // Available stores for Add Store dropdown. Menu templates can only be
  // added at stores with menu tickets on (the backend rejects the rest).
  const availableStoresToAdd = (storeNames || []).filter(
    (s) =>
      !existingStoreIds.includes(s.id) &&
      !storesToAdd.includes(s.id) &&
      (category !== "menu" || s.menu_tickets_enabled)
  );

  // Stores a single template can move to. A menu template only moves to
  // stores with menu tickets on; its current store always stays listed.
  const moveStoreOptions = (storeNames || []).filter(
    (s) =>
      category !== "menu" ||
      s.menu_tickets_enabled ||
      s.id === editingGroup?.templates?.[0]?.store?.id
  );

  const openEditGroup = (group) => {
    const first = group.templates[0];
    setEditForm({
      title: first.title,
      description: first.description,
      issue_type: first.issue_type,
      priority: first.priority,
      recurrence_interval: first.recurrence_interval,
      recurrence_count: first.recurrence_count,
      recurrence_trigger: first.recurrence_trigger,
      start_date: first.start_date ? new Date(first.start_date + "T00:00:00") : null,
      is_active: first.is_active,
      assigned_to: first.assigned_to?.id || "",
      store_id: first.store?.id,
    });
    setStoresToAdd([]);
    setStoresToRemove([]);
    setActiveToggled(false);
    setEditingGroup(group);
    if (!group.isMultiStore && first.store?.id) {
      onStoreChange?.(first.store.id);
    }
  };

  const handleUpdate = async () => {
    if (!optionsReady) {
      toast.error("Ticket options are still loading. Please wait or retry.");
      return;
    }
    if (!editForm.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setIsSubmitting(true);
    try {
      const startDateStr = editForm.start_date
        ? `${editForm.start_date.getFullYear()}-${String(editForm.start_date.getMonth() + 1).padStart(2, "0")}-${String(editForm.start_date.getDate()).padStart(2, "0")}`
        : null;

      const sharedPayload = {
        category: category || "maintenance",
        title: editForm.title,
        description: editForm.description,
        issue_type: editForm.issue_type,
        priority: editForm.priority,
        recurrence_interval: editForm.recurrence_interval,
        recurrence_count: editForm.recurrence_count,
        recurrence_trigger: editForm.recurrence_trigger,
        start_date: startDateStr,
        ...(activeToggled ? { is_active: editForm.is_active } : {}),
      };

      let totalSucceeded = 0;
      let totalFailed = 0;

      // Phase 1: Create templates for storesToAdd
      if (storesToAdd.length > 0) {
        const createResults = await Promise.allSettled(
          storesToAdd.map((storeId) =>
            postRightworkAPI(API_post_recurring_maintenance_template, {
              store_id: storeId,
              ...sharedPayload,
            })
          )
        );
        totalSucceeded += createResults.filter((r) => r.status === "fulfilled").length;
        totalFailed += createResults.filter((r) => r.status === "rejected").length;

        // Backend ignores is_active on create, so pause newly created templates if group is inactive.
        // If user toggled the switch, use their choice; otherwise derive from remaining templates.
        const remainingTemplates = editingGroup.templates.filter(
          (t) => !storesToRemove.includes(t.uuid)
        );
        const remainingAllActive = remainingTemplates.length > 0 && remainingTemplates.every((t) => t.is_active);
        const shouldPauseNew = activeToggled ? !editForm.is_active : !remainingAllActive;
        if (shouldPauseNew) {
          const newTemplates = createResults
            .filter((r) => r.status === "fulfilled")
            .map((r) => r.value?.data?.redux?.payload?.recurring_maintenance_template)
            .filter(Boolean);
          const pauseResults = await Promise.allSettled(
            newTemplates.map((t) =>
              postRightworkAPI(API_update_recurring_maintenance_template, {
                store_id: t.store?.id,
                recurring_maintenance_template_uuid: t.uuid,
                is_active: false,
              })
            )
          );
          totalFailed += pauseResults.filter((r) => r.status === "rejected").length;
        }
      }

      // Phase 2: Archive templates for storesToRemove
      if (storesToRemove.length > 0) {
        const archiveResults = await Promise.allSettled(
          storesToRemove.map((templateUuid) => {
            const t = editingGroup.templates.find((tmpl) => tmpl.uuid === templateUuid);
            return postRightworkAPI(API_delete_recurring_maintenance_template, {
              store_id: t?.store?.id,
              recurring_maintenance_template_uuid: templateUuid,
            });
          })
        );
        totalSucceeded += archiveResults.filter((r) => r.status === "fulfilled").length;
        totalFailed += archiveResults.filter((r) => r.status === "rejected").length;
      }

      // Phase 3: Update remaining templates with shared field changes
      const templatesToUpdate = editingGroup.templates.filter(
        (t) => !storesToRemove.includes(t.uuid)
      );

      if (templatesToUpdate.length > 0) {
        const isSingleStoreResult = effectiveStoreCount === 1 && storesToAdd.length === 0 && storesToRemove.length === 0;

        if (isSingleStoreResult) {
          const template = templatesToUpdate[0];
          await postRightworkAPI(API_update_recurring_maintenance_template, {
            store_id: homeStore,
            recurring_maintenance_template_uuid: template.uuid,
            ...sharedPayload,
            assigned_to_store_team_member_id: editForm.assigned_to || null,
            new_store_id: editForm.store_id !== template.store?.id ? editForm.store_id : undefined,
          });
          totalSucceeded += 1;
        } else {
          const updateResults = await Promise.allSettled(
            templatesToUpdate.map((t) =>
              postRightworkAPI(API_update_recurring_maintenance_template, {
                store_id: t.store?.id,
                recurring_maintenance_template_uuid: t.uuid,
                ...sharedPayload,
              })
            )
          );
          totalSucceeded += updateResults.filter((r) => r.status === "fulfilled").length;
          totalFailed += updateResults.filter((r) => r.status === "rejected").length;
        }
      }

      if (totalFailed === 0) {
        toast.success(
          effectiveStoreCount > 1
            ? `Updated recurring ticket across ${effectiveStoreCount} stores`
            : "Recurring ticket updated"
        );
      } else {
        toast.error(`${totalSucceeded} succeeded, ${totalFailed} failed`);
      }

      setEditingGroup(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.system?.message || "Failed to update recurring ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async () => {
    const count = editingGroup.templates.length;
    const message = count > 1
      ? `Are you sure you want to archive this recurring ticket across ${count} stores? Existing tickets will not be affected.`
      : "Are you sure you want to archive this recurring ticket? Existing tickets will not be affected.";
    if (!window.confirm(message)) return;
    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        editingGroup.templates.map((t) =>
          postRightworkAPI(API_delete_recurring_maintenance_template, {
            store_id: t.store?.id,
            recurring_maintenance_template_uuid: t.uuid,
          })
        )
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast.success(count > 1 ? `Archived recurring ticket across ${succeeded} stores` : "Recurring ticket archived");
      } else {
        toast.error(`Archived ${succeeded} of ${count}. ${failed} failed.`);
      }
      setEditingGroup(null);
      onRefresh();
    } catch (error) {
      toast.error("Failed to archive recurring ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEditingGroup(null);
    setStoresToAdd([]);
    setStoresToRemove([]);
    setActiveToggled(false);
    onClose();
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <div className="w-[500px] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          {editingGroup ? (
            <>
              {/* Edit mode */}
              <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3">
                <button onClick={() => { setEditingGroup(null); setStoresToAdd([]); setStoresToRemove([]); setActiveToggled(false); }} className="text-slate-500 hover:text-slate-700">
                  <ArrowBackIcon fontSize="small" />
                </button>
                <h2 className="text-lg font-semibold text-slate-900">
                  {effectiveIsMultiStore
                    ? `Edit Recurring Ticket (${effectiveStoreCount} stores)`
                    : "Edit Recurring Ticket"}
                </h2>
              </div>

              <div className="px-6 mt-4">
                {/* Title */}
                <div>
                  <p className="text-sm text-slate-500">Title</p>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={editForm.title}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                      sx={textFieldSx}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Description</p>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      value={editForm.description}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                      sx={textFieldSx}
                    />
                  </div>
                </div>

                {/* Issue Type */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Issue Type</p>
                  <div className="mt-1">
                    <Select
                      fullWidth
                      value={editForm.issue_type}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, issue_type: e.target.value }))}
                      size="small"
                      sx={selectSx}
                    >
                      {issueTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Priority */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Priority</p>
                  {!optionsReady && (
                    <p className="text-xs text-amber-700 mt-1">
                      {optionsStatus === "error" ? (
                        <>
                          Couldn't load this company's ticket options.{" "}
                          <button type="button" className="underline font-medium" onClick={onRetryOptions}>
                            Retry
                          </button>
                        </>
                      ) : (
                        "Loading ticket options…"
                      )}
                    </p>
                  )}
                  <div className="mt-1">
                    <Select
                      fullWidth
                      value={editForm.priority}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, priority: e.target.value }))}
                      disabled={!optionsReady}
                      size="small"
                      sx={selectSx}
                    >
                      {priorityOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Store & Assigned To — single-store view (only for originally single-store groups with no pending adds) */}
                {!editingGroup.isMultiStore && !effectiveIsMultiStore && (
                  <>
                    {storeNames?.length > 1 && (
                      <div className="mt-5">
                        <p className="text-sm text-slate-500">Store</p>
                        <div className="mt-1">
                          <Select
                            fullWidth
                            value={editForm.store_id || ""}
                            onChange={(e) => {
                              setEditForm((prev) => ({ ...prev, store_id: e.target.value, assigned_to: "" }));
                              onStoreChange?.(e.target.value);
                            }}
                            size="small"
                            sx={selectSx}
                          >
                            {moveStoreOptions.map((s) => (
                              <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    )}

                    {employees?.length > 0 && (
                      <div className="mt-5">
                        <p className="text-sm text-slate-500">Assigned To</p>
                        <div className="mt-1">
                          <Select
                            fullWidth
                            value={editForm.assigned_to || ""}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, assigned_to: e.target.value }))}
                            size="small"
                            displayEmpty
                            sx={selectSx}
                          >
                            <MenuItem value="">
                              <span className="text-slate-400">Unassigned</span>
                            </MenuItem>
                            {employees.map((emp) => (
                              <MenuItem key={emp.id} value={emp.id}>{emp.full_name}</MenuItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Interactive store list — shown for any multi-store group or when stores have been added */}
                {(editingGroup.isMultiStore || effectiveIsMultiStore) && (
                  <div className="mt-5">
                    <p className="text-sm text-slate-500">Stores</p>
                    <div className="mt-1 border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
                      {/* Existing stores */}
                      {editingGroup.templates.map((t) => {
                        const isPendingRemoval = storesToRemove.includes(t.uuid);
                        return (
                          <div key={t.uuid} className={`px-3 py-2.5 flex items-center justify-between ${isPendingRemoval ? "opacity-40" : ""}`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${t.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                              <span className={`text-sm text-slate-700 ${isPendingRemoval ? "line-through" : ""}`}>{t.store?.name}</span>
                            </div>
                            <button
                              onClick={() => {
                                if (isPendingRemoval) {
                                  setStoresToRemove((prev) => prev.filter((id) => id !== t.uuid));
                                } else {
                                  setStoresToRemove((prev) => [...prev, t.uuid]);
                                }
                              }}
                              disabled={!isPendingRemoval && effectiveStoreCount <= 1}
                              className={`text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed`}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </button>
                          </div>
                        );
                      })}
                      {/* Pending-add stores */}
                      {storesToAdd.map((storeId) => {
                        const storeName = storeNames?.find((s) => s.id === storeId)?.store_name || "Unknown";
                        return (
                          <div key={`add-${storeId}`} className="px-3 py-2.5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              <span className="text-sm text-slate-700">{storeName}</span>
                              <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-medium">new</span>
                            </div>
                            <button
                              onClick={() => setStoresToAdd((prev) => prev.filter((id) => id !== storeId))}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Add Store dropdown — always available when there are stores to add */}
                {availableStoresToAdd.length > 0 && (
                  <div className="mt-3">
                    <Select
                      fullWidth
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setStoresToAdd((prev) => [...prev, e.target.value]);
                        }
                      }}
                      size="small"
                      displayEmpty
                      sx={selectSx}
                    >
                      <MenuItem value="" disabled>
                        <span className="text-slate-400">+ Add Store...</span>
                      </MenuItem>
                      {availableStoresToAdd.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>
                      ))}
                    </Select>
                  </div>
                )}

                {/* Recurrence */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Recurrence</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-slate-600">Every</span>
                    <TextField
                      type="number"
                      value={editForm.recurrence_count}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, recurrence_count: e.target.value === "" ? "" : parseInt(e.target.value) || "" }))}
                      onBlur={() => setEditForm((prev) => ({ ...prev, recurrence_count: Math.max(1, parseInt(prev.recurrence_count) || 1) }))}
                      size="small"
                      inputProps={{ min: 1, style: { textAlign: "center" } }}
                      sx={{ ...textFieldSx, width: 70 }}
                    />
                    <Select
                      value={editForm.recurrence_interval}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, recurrence_interval: e.target.value }))}
                      size="small"
                      sx={{ ...selectSx, minWidth: 110 }}
                    >
                      {RECURRENCE_INTERVAL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Start Date */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Start Date</p>
                  <div className="mt-1">
                    <ThemeProvider theme={violetTheme}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={editForm.start_date}
                          onChange={(newValue) => setEditForm((prev) => ({ ...prev, start_date: newValue }))}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  height: "45px",
                                  backgroundColor: "white",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#6d28d9",
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                  </div>
                </div>

                {/* Trigger Mode */}
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Trigger Mode</p>
                  <div className="mt-1 flex gap-2">
                    <button
                      type="button"
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        editForm.recurrence_trigger === "on_completion"
                          ? "border-violet-600 bg-violet-50 text-violet-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setEditForm((prev) => ({ ...prev, recurrence_trigger: "on_completion" }))}
                    >
                      After completion
                    </button>
                    <button
                      type="button"
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        editForm.recurrence_trigger === "fixed"
                          ? "border-violet-600 bg-violet-50 text-violet-700"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setEditForm((prev) => ({ ...prev, recurrence_trigger: "fixed" }))}
                    >
                      Fixed schedule
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {(() => {
                      const count = parseInt(editForm.recurrence_count) || 1;
                      const units = { daily: ["day", "days"], weekly: ["week", "weeks"], monthly: ["month", "months"] };
                      const unit = (units[editForm.recurrence_interval] || units.daily)[count === 1 ? 0 : 1];
                      const cadence = count === 1 ? `1 ${unit}` : `${count} ${unit}`;
                      return editForm.recurrence_trigger === "on_completion"
                        ? `Next ticket created ${cadence} after the current one is completed`
                        : `Creates a ticket every ${cadence} regardless of completion`;
                    })()}
                  </p>
                </div>

                {/* Active Toggle */}
                <div className="mt-5">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Active</p>
                      <p className="text-xs text-slate-400">{editForm.is_active ? "Recurring ticket is active" : "Recurring ticket is paused"}</p>
                    </div>
                    <Switch
                      checked={editForm.is_active}
                      onChange={(e) => { setEditForm((prev) => ({ ...prev, is_active: e.target.checked })); setActiveToggled(true); }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#6d28d9" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#6d28d9" },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Spacer */}
              <div className="h-[200px]" />

              <DrawerFooterContainerLarge>
                <div className="w-full space-y-2">
                  <ButtonFilled
                    text={isSubmitting ? "Saving..." : (effectiveIsMultiStore ? `Update Recurring Ticket (${effectiveStoreCount} stores)` : "Update Recurring Ticket")}
                    onClick={isSubmitting ? undefined : handleUpdate}
                  />
                  <ButtonFilledCancel text="Archive Recurring Ticket" onClick={handleArchive} />
                </div>
              </DrawerFooterContainerLarge>
            </>
          ) : (
            <>
              {/* List mode */}
              <DrawerHeader
                title={`Recurring ${CATEGORY_CONFIG[category]?.label || "Maintenance"} Tickets`}
                onClose={handleClose}
              />

              <div className="px-6">
                {isLoading ? (
                  <div className="flex items-center justify-center pt-32 pb-16">
                    <CircularProgress size={36} sx={{ color: "#6d28d9" }} />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <RepeatIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
                    <p className="text-base font-medium">No recurring {CATEGORY_CONFIG[category]?.label || "Maintenance"} tickets</p>
                    <p className="text-sm mt-1">Create one using the "Make this recurring" toggle in the New Ticket form</p>
                  </div>
                ) : (
                  <div className="space-y-3 mt-2">
                    {groupedTemplates.map((group) => (
                      <div
                        key={group.groupKey}
                        className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-violet-300 transition-colors"
                        onClick={() => openEditGroup(group)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900">{group.title}</p>
                              <div className={`w-2 h-2 rounded-full ${
                                group.allActive ? "bg-emerald-500" : group.anyActive ? "bg-amber-500" : "bg-slate-300"
                              }`} />
                              {group.isMultiStore && (
                                <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                                  {group.storeCount} stores
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{group.sharedFields.cadence_display}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-2.5">
                          <IssueTypeBadge issueType={group.sharedFields.issue_type} />
                          {optionsReady ? (
                            <PriorityBadge priority={group.sharedFields.priority} label={priorityLabelFor(group.sharedFields.priority)} />
                          ) : (
                            <span className="text-[13px] text-slate-400" title="Loading priority labels">…</span>
                          )}
                          {group.sharedFields.recurrence_trigger === "on_completion" && (
                            <div className="rounded-lg px-2 py-0.5 bg-amber-50 border border-amber-200">
                              <p className="text-xs font-medium text-amber-700">On completion</p>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 border-t border-slate-100 pt-2 space-y-1.5">
                          {group.templates.map((t) => (
                            <div key={t.uuid} className="grid items-center text-xs" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full ${t.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                                <span className="text-slate-600">{t.store?.name}</span>
                              </div>
                              <span className="text-slate-400 text-center">{t.generated_ticket_count || 0} ticket{t.generated_ticket_count !== 1 ? "s" : ""}</span>
                              <span className="text-slate-400 text-right">{t.next_due_date ? `Next: ${t.next_due_date}` : "Waiting"}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}

export default RecurringManagementDrawer;
